/* journeyShareCard.js — generate a 1080x1920 Instagram-story PNG for a saved journey */

const W = 1080;
const H = 1920;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapLines(ctx, text, maxWidth) {
  const words = (text || '').split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? cur + ' ' + w : w;
    if (ctx.measureText(test).width <= maxWidth) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function drawWrapped(ctx, text, x, y, maxWidth, lineHeight, maxLines = 0) {
  const lines = wrapLines(ctx, text, maxWidth);
  const slice = maxLines ? lines.slice(0, maxLines) : lines;
  if (maxLines && lines.length > maxLines && slice.length) {
    slice[slice.length - 1] = slice[slice.length - 1].replace(/[.,]?$/, '…');
  }
  slice.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineHeight));
  return y + slice.length * lineHeight;
}

export async function buildJourneyShareCard(journey, { coverDataUrl } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // 1) Cover image as background (cover-fit), dark gradient overlay
  if (coverDataUrl) {
    try {
      const img = await loadImage(coverDataUrl);
      const ratio = Math.max(W / img.width, H / img.height);
      const dw = img.width * ratio;
      const dh = img.height * ratio;
      ctx.filter = 'blur(2px) saturate(1.05)';
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      ctx.filter = 'none';
    } catch {/**/}
  } else {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#3D1A78');
    g.addColorStop(1, '#D06892');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  // Dark gradient overlay for legibility
  const ov = ctx.createLinearGradient(0, 0, 0, H);
  ov.addColorStop(0, 'rgba(20,5,60,0.78)');
  ov.addColorStop(0.5, 'rgba(20,5,60,0.55)');
  ov.addColorStop(1, 'rgba(0,0,0,0.92)');
  ctx.fillStyle = ov;
  ctx.fillRect(0, 0, W, H);

  // 2) Top brand
  ctx.fillStyle = '#FCD34D';
  ctx.font = 'bold 32px system-ui, -apple-system, "Helvetica Neue"';
  ctx.textAlign = 'center';
  ctx.fillText('✨ FOCUS CRITTER · TIDSRESOR', W / 2, 110);

  // 3) Role label
  if (journey.role_label) {
    ctx.fillStyle = '#A78BFA';
    ctx.font = 'bold 36px system-ui';
    ctx.fillText('📍 ' + journey.role_label.toUpperCase(), W / 2, 200);
  }

  // 4) Big hook headline (serif)
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 96px Georgia, "Times New Roman", serif';
  ctx.textAlign = 'center';
  let y = 360;
  const hookLines = wrapLines(ctx, journey.clip_hook || '', W - 140);
  hookLines.slice(0, 3).forEach((ln) => { ctx.fillText(ln, W / 2, y); y += 110; });

  // 5) Subtitle
  ctx.fillStyle = '#FBCFE8';
  ctx.font = 'italic 36px Georgia, serif';
  y += 10;
  drawWrapped(ctx, journey.clip_subtitle || '', W / 2, y, W - 200, 50, 2);
  y += 130;

  // 6) "Mina val" section
  const scenes = (journey.scenes || []).filter((s) => s.choice);
  if (scenes.length) {
    ctx.fillStyle = '#FCD34D';
    ctx.font = 'bold 30px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('🎬 MINA VAL', W / 2, y);
    y += 60;

    ctx.fillStyle = '#FFFFFF';
    ctx.font = '500 36px Georgia, serif';
    ctx.textAlign = 'left';
    const pick = scenes.slice(0, 3);
    for (const s of pick) {
      const arrowX = 100;
      const textX = 150;
      ctx.fillStyle = '#F472B6';
      ctx.fillText('→', arrowX, y);
      ctx.fillStyle = '#FFFFFF';
      y = drawWrapped(ctx, s.choice, textX, y, W - 250, 50, 2);
      y += 30;
    }
  }

  // 7) Finale text
  if (journey.final_narration) {
    y += 30;
    ctx.fillStyle = '#FCD34D';
    ctx.font = 'bold 28px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('— SLUTET —', W / 2, y);
    y += 60;
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'italic 36px Georgia, serif';
    y = drawWrapped(ctx, journey.final_narration, W / 2, y, W - 160, 52, 5);
  }

  // 8) Footer pill
  ctx.fillStyle = 'rgba(124,95,204,0.85)';
  const pillW = 760;
  const pillH = 90;
  const px = (W - pillW) / 2;
  const py = H - 160;
  // Rounded rect
  const r = 45;
  ctx.beginPath();
  ctx.moveTo(px + r, py);
  ctx.lineTo(px + pillW - r, py);
  ctx.quadraticCurveTo(px + pillW, py, px + pillW, py + r);
  ctx.lineTo(px + pillW, py + pillH - r);
  ctx.quadraticCurveTo(px + pillW, py + pillH, px + pillW - r, py + pillH);
  ctx.lineTo(px + r, py + pillH);
  ctx.quadraticCurveTo(px, py + pillH, px, py + pillH - r);
  ctx.lineTo(px, py + r);
  ctx.quadraticCurveTo(px, py, px + r, py);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 38px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('🚀 Spela själv på sofia.ruus', W / 2, py + 58);

  // Year sticker top-right
  if (journey.clip_year) {
    ctx.fillStyle = 'rgba(252,211,77,0.95)';
    ctx.font = 'bold 28px system-ui';
    ctx.textAlign = 'right';
    ctx.fillText(journey.clip_year.toUpperCase(), W - 60, 60);
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

export async function shareOrDownload(journey, coverDataUrl) {
  const blob = await buildJourneyShareCard(journey, { coverDataUrl });
  if (!blob) throw new Error('Kunde inte skapa bild');
  const safeName = (journey.clip_hook || 'tidsresa').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
  const fileName = `tidsresa-${safeName}.png`;
  const file = new File([blob], fileName, { type: 'image/png' });

  // Try native share on mobile
  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: journey.clip_hook || 'Min tidsresa',
        text: 'Jag reste tillbaka i tiden 💜✨',
      });
      return { shared: true };
    } catch (e) {
      if (e?.name === 'AbortError') return { shared: false, cancelled: true };
      // fall through to download
    }
  }

  // Fallback: download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return { downloaded: true };
}
